import { useEffect, useMemo, useRef, useState } from "react";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import { getStorageJSON, sanitizeText } from "../../utils/helpers";
import { addDoc, collection, doc, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../../firebase/firestore";

const FAQ_LIST = [
  {
    q: "What is your delivery time?",
    a: "Orders placed before 10 PM are delivered next day. After 10 PM, delivery is within 48 hours.",
  },
  {
    q: "How can I track my order?",
    a: "Go to My Orders in the menu. You can also check notification updates in the bell icon.",
  },
  {
    q: "How do coupon codes work?",
    a: "Open Offers page, copy active coupon and apply it during checkout as per min order and expiry rules.",
  },
  {
    q: "How do I change my delivery address?",
    a: "Open Settings and update flat, building, road, landmark and pin code details.",
  },
];

export default function Support() {
  const user = getStorageJSON("user", null);
  const supportUserId = useMemo(
    () => user?.uid || `guest-${(user?.email || "anonymous").replace(/[^a-z0-9]/gi, "").toLowerCase()}`,
    [user?.uid, user?.email]
  );
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("");
  const [faqOpen, setFaqOpen] = useState(0);
  const [chatInput, setChatInput] = useState("");
  const [chat, setChat] = useState([]);
  const [chatLoading, setChatLoading] = useState(true);
  const [chatError, setChatError] = useState("");
  const [sendingChat, setSendingChat] = useState(false);
  const [unreadAgentCount, setUnreadAgentCount] = useState(0);
  const [contactConfig, setContactConfig] = useState({
    supportPhone: "7977788566",
    supportWhatsApp: "8454874147",
  });
  const chatBottomRef = useRef(null);

  const supportStorageKey = useMemo(() => `supportChat_${supportUserId}`, [supportUserId]);
  const supportReadKey = useMemo(() => `supportChatRead_${supportUserId}`, [supportUserId]);

  useEffect(() => {
    const ref = doc(db, "appConfig", "platform");
    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) return;
        setContactConfig((prev) => ({ ...prev, ...snap.data() }));
      },
      () => {
        // Keep defaults when config read fails.
      }
    );
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    setChatLoading(true);
    setChatError("");

    const fallback = getStorageJSON(supportStorageKey, []);
    if (Array.isArray(fallback) && fallback.length > 0) {
      setChat(fallback);
      setChatLoading(false);
      const lastReadAtMs = Number(localStorage.getItem(supportReadKey) || 0);
      const unreadFromCache = fallback.filter(
        (item) => item.role === "agent" && Number(item.createdAtMs || 0) > lastReadAtMs
      ).length;
      setUnreadAgentCount(unreadFromCache);
    }

    const supportQuery = query(collection(db, "support"), where("userId", "==", supportUserId));
    const unsub = onSnapshot(
      supportQuery,
      (snap) => {
        const mapped = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => Number(a.createdAtMs || 0) - Number(b.createdAtMs || 0))
          .map((msg) => ({
            id: msg.id,
            role: msg.fromAdmin ? "agent" : "user",
            text: msg.message || "",
            image: msg.image || "",
            createdAtMs: Number(msg.createdAtMs || Date.now()),
          }));

        const nextChat = mapped.length
          ? mapped
          : [{ id: "welcome", role: "agent", text: "Hello, this is customer care. How can we help you today?", image: "", createdAtMs: Date.now() }];

        const lastReadAtMs = Number(localStorage.getItem(supportReadKey) || 0);
        const unreadCount = nextChat.filter(
          (item) => item.role === "agent" && Number(item.createdAtMs || 0) > lastReadAtMs
        ).length;

        setChat(nextChat);
        setUnreadAgentCount(unreadCount);
        localStorage.setItem(supportStorageKey, JSON.stringify(nextChat));
        setChatLoading(false);
        setChatError("");
      },
      () => {
        const cached = getStorageJSON(supportStorageKey, []);
        if (Array.isArray(cached) && cached.length > 0) {
          setChat(cached);
          const lastReadAtMs = Number(localStorage.getItem(supportReadKey) || 0);
          const unreadFromCache = cached.filter(
            (item) => item.role === "agent" && Number(item.createdAtMs || 0) > lastReadAtMs
          ).length;
          setUnreadAgentCount(unreadFromCache);
        }
        setChatLoading(false);
        setChatError("Realtime support is temporarily unavailable. Showing saved messages.");
      }
    );

    return () => unsub();
  }, [supportReadKey, supportStorageKey, supportUserId]);

  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chat]);

  useEffect(() => {
    const latestAgentAtMs = chat
      .filter((entry) => entry.role === "agent")
      .reduce((max, entry) => Math.max(max, Number(entry.createdAtMs || 0)), 0);

    if (!latestAgentAtMs) return;
    localStorage.setItem(supportReadKey, String(latestAgentAtMs));
    setUnreadAgentCount(0);
    window.dispatchEvent(
      new CustomEvent("support-chat-read", {
        detail: { userId: supportUserId, lastReadAtMs: latestAgentAtMs },
      })
    );
  }, [chat, supportReadKey, supportUserId]);

  const submitTicket = (event) => {
    event.preventDefault();
    setStatus("");

    const cleanedSubject = sanitizeText(subject, 80);
    const cleanedMessage = sanitizeText(message, 600);

    if (cleanedSubject.length < 4 || cleanedMessage.length < 8) {
      setStatus("Please provide a clear subject and message.");
      return;
    }

    const tickets = getStorageJSON("supportTickets", []);

    const ticket = {
      id: `S-${Date.now()}`,
      userId: supportUserId,
      name: user?.name || "Guest User",
      phone: user?.phone || "",
      subject: cleanedSubject,
      message: `Ticket: ${cleanedSubject}\n${cleanedMessage}`,
      status: "Open",
      fromAdmin: false,
      createdAt: new Date().toISOString(),
      createdAtMs: Date.now(),
    };

    localStorage.setItem("supportTickets", JSON.stringify([ticket, ...(Array.isArray(tickets) ? tickets : [])]));
    addDoc(collection(db, "support"), ticket).catch(() => {
      // Keep local fallback if Firestore write fails.
    });
    setSubject("");
    setMessage("");
    setStatus("Support ticket submitted successfully.");
  };

  const sendChatMessage = async () => {
    const clean = sanitizeText(chatInput, 220);
    if (!clean || sendingChat) return;

    setChatInput("");
    setSendingChat(true);
    setChatError("");

    const optimistic = {
      id: `local-${Date.now()}`,
      role: "user",
      text: clean,
      image: "",
      createdAtMs: Date.now(),
      deliveryStatus: "sending",
      retryableText: clean,
    };

    setChat((prev) => {
      const next = [...prev, optimistic];
      localStorage.setItem(supportStorageKey, JSON.stringify(next));
      return next;
    });

    try {
      await addDoc(collection(db, "support"), {
        userId: supportUserId,
        name: user?.name || "Guest User",
        phone: user?.phone || "",
        message: clean,
        fromAdmin: false,
        createdAt: new Date().toISOString(),
        createdAtMs: Date.now(),
      });

      setChat((prev) => {
        const next = prev.map((entry) =>
          entry.id === optimistic.id
            ? { ...entry, deliveryStatus: "sent" }
            : entry
        );
        localStorage.setItem(supportStorageKey, JSON.stringify(next));
        return next;
      });
    } catch {
      setChat((prev) => {
        const next = prev.map((entry) =>
          entry.id === optimistic.id
            ? { ...entry, deliveryStatus: "failed" }
            : entry
        );
        localStorage.setItem(supportStorageKey, JSON.stringify(next));
        return next;
      });
      setChatError("Message failed. Tap Retry on the message to resend.");
    } finally {
      setSendingChat(false);
    }

  };

  const retryChatMessage = async (entryId) => {
    const target = chat.find((entry) => entry.id === entryId);
    const retryText = sanitizeText(target?.retryableText || target?.text || "", 220);
    if (!retryText || sendingChat) return;

    setSendingChat(true);
    setChatError("");
    setChat((prev) => prev.map((entry) => (entry.id === entryId ? { ...entry, deliveryStatus: "sending" } : entry)));

    try {
      await addDoc(collection(db, "support"), {
        userId: supportUserId,
        name: user?.name || "Guest User",
        phone: user?.phone || "",
        message: retryText,
        fromAdmin: false,
        createdAt: new Date().toISOString(),
        createdAtMs: Date.now(),
      });

      setChat((prev) => {
        const next = prev.map((entry) =>
          entry.id === entryId
            ? { ...entry, deliveryStatus: "sent" }
            : entry
        );
        localStorage.setItem(supportStorageKey, JSON.stringify(next));
        return next;
      });
    } catch {
      setChat((prev) => {
        const next = prev.map((entry) =>
          entry.id === entryId
            ? { ...entry, deliveryStatus: "failed" }
            : entry
        );
        localStorage.setItem(supportStorageKey, JSON.stringify(next));
        return next;
      });
      setChatError("Retry failed. Please check internet and try again.");
    } finally {
      setSendingChat(false);
    }
  };

  const supportPhone = String(contactConfig.supportPhone || "7977788566").replace(/\D/g, "").slice(-10);
  const supportWhatsApp = String(contactConfig.supportWhatsApp || "8454874147").replace(/\D/g, "").slice(-10);

  return (
    <div className="home">
      <Navbar />

      <div className="customer-page">
        <div className="customer-page-header">
          <h2>Support Center</h2>
          <p>Tickets, FAQ and live customer care chat in one place.</p>
        </div>

        <section className="customer-card settings-section">
          <h3>Quick Contact</h3>
          <div className="settings-actions">
            <a className="primary-action inline-link" href={`tel:+91${supportPhone || "7977788566"}`}>Call Customer Care</a>
            <a className="secondary-action inline-link" href={`https://wa.me/91${supportWhatsApp || "8454874147"}?text=Hi%20Support%20Team`} target="_blank" rel="noreferrer">Chat on WhatsApp</a>
          </div>
        </section>

        <section className="customer-card settings-section">
          <h3>FAQ</h3>
          <div className="faq-list">
            {FAQ_LIST.map((item, index) => (
              <article key={item.q} className="faq-item">
                <button className="faq-q" onClick={() => setFaqOpen(index === faqOpen ? -1 : index)}>
                  {item.q}
                </button>
                {faqOpen === index && <p className="faq-a">{item.a}</p>}
              </article>
            ))}
          </div>
        </section>

        <section className="customer-card settings-section">
          <div className="row-between">
            <h3>Live Chat (Beta)</h3>
            {unreadAgentCount > 0 ? <span className="status-chip">{unreadAgentCount} new</span> : null}
          </div>
          <div className="support-chat-box">
            <div className="support-chat-feed">
              {chatLoading ? <p className="support-chat-meta">Loading chat...</p> : null}
              {!chatLoading && chatError ? <p className="support-chat-meta support-chat-error">{chatError}</p> : null}
              {!chatLoading && !chat.length ? <p className="support-chat-meta">No messages yet.</p> : null}
              {chat.map((entry, idx) => (
                <div key={entry.id || `${entry.role}-${idx}`} className={`chat-line ${entry.role}`}>
                  {entry.image ? <img src={entry.image} alt="support" className="chat-line-image" /> : null}
                  {entry.text ? <p>{entry.text}</p> : null}
                  {entry.role === "user" ? (
                    <div className="chat-delivery-row">
                      <span className={`chat-delivery-badge ${entry.deliveryStatus || "sent"}`}>
                        {entry.deliveryStatus === "failed"
                          ? "Failed"
                          : entry.deliveryStatus === "sending"
                            ? "Sending"
                            : "Sent"}
                      </span>
                      {entry.deliveryStatus === "failed" ? (
                        <button
                          type="button"
                          className="chat-retry-btn"
                          onClick={() => retryChatMessage(entry.id)}
                        >
                          Retry
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                  <small>{new Date(Number(entry.createdAtMs || Date.now())).toLocaleTimeString()}</small>
                </div>
              ))}
              <div ref={chatBottomRef} />
            </div>
            <div className="support-chat-input">
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder="Type your message"
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    sendChatMessage();
                  }
                }}
              />
              <button className="primary-action" type="button" onClick={sendChatMessage} disabled={sendingChat}>
                {sendingChat ? "Sending..." : "Send"}
              </button>
            </div>
          </div>
        </section>

        <form className="customer-form" onSubmit={submitTicket}>
          <h3>Raise a Support Ticket</h3>
          <label>Subject</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={80}
            required
          />

          <label>Message</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={5}
            maxLength={600}
            required
          />

          {status && <p className="form-status">{status}</p>}

          <button type="submit" className="primary-action">Submit Ticket</button>
        </form>
      </div>

      <Footer />
    </div>
  );
}
